'use client';

import { animate, motion, useInView } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';

import { arcBox, arcPath, lengthToDeg, polar } from '@/lib/arc';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Kademeli gösterge yayı — ekran görüntüsünün karşılığı.
 *
 * Kaynak: `Ekran Resmi 2026-08-11 09.49.12.png`. Ölçüm yöntemi bir öncekiyle
 * aynı (bkz. `progress-rail.tsx`): eşik değil, ışın profillerinin %50
 * geçişleri ve mürekkep integrali. Burada çemberin kendisi de bilinmezdi —
 * merkez ve yarıçap halkanın orta çizgisine en küçük karelerle oturtuldu
 * (artık std 2.6px, R = 593).
 *
 * ── Çözülen tasarım ───────────────────────────────────────────────────────
 *
 * Yay eşit açılı bölümlere ayrılmış ve her bölüm bir öncekinden hem KALIN hem
 * KOYU. Değerin düştüğü bölüm — "baş" — siyah; ondan sonrası ince ve çok açık
 * bir ray. Yani yay ilerledikçe hem ağırlaşıyor hem koyulaşıyor: doluluk iki
 * kanaldan birden okunuyor.
 *
 * 🔑 Bölümler ortak DIŞ çemberde hizalı; kalınlık İÇERİ doğru büyüyor.
 * Ölçülen dış yarıçaplar 587.93 / 587.27 / 587.33 / 587.55 — dört bölümde de
 * aynı. İlk çözümde her bölüm kendi orta çizgisine oturtulmuştu ve siyah baş
 * gözle görülür biçimde "aşağı kaymış" duruyordu; hata buradaydı. Ortak
 * merkez + bölüm başına yarıçap birlikte çözülünce artık 2.6px'ten 0.60px'e
 * indi ve dış hizanın sabit olduğu ortaya çıktı.
 *
 * 🔑 Boşluklar ve topuz çapı AÇI değil UZUNLUK. Bölüm araları yayın her
 * yerinde ~7px, yani sabit bir yay uzunluğu; açı olarak yazılsalardı yarıçap
 * ya da yay açısı değişince incelip kalınlaşırlardı. `lengthToDeg` bunun için.
 *
 * 🔑 Topuz değerin İLERİSİNDE. Dolgu değer açısında bitiyor, topuz ondan
 * yarım boşluk sonra teğet duruyor. Tersi denendi ve tutmadı: topuzun merkezi
 * değer kabul edilirse doluluk %73.1 çıkıyor, oysa etikette 71,74 yazıyor.
 *
 * ── Yay açısı nasıl bulundu ───────────────────────────────────────────────
 *
 * Kadraj yayın iki ucunu da kesiyordu, yani açı doğrudan görünmüyordu. Üç
 * bölüm sınırı taraflı olmayan yöntemle ölçülünce (her bölümün kendi
 * mürekkebinin %50 noktası; en düşük noktayı almak ağır komşuya doğru
 * kayıyor) −61.66 / −23.89 / +13.88 çıktı — aralar 37.77 ve 37.77, TAM eşit.
 * Adım buysa başlangıç `−61.66 − k × 37.77`, bitiş de `başlangıç + N × 37.77`.
 * Etiketteki 71,74 bu iki bilinmezi tek bir çözüme kilitliyor: yalnızca
 * N = 6, k = 2 değeri tutturuyor (%71.87 — 0.13 puan sapma). Diğer adaylar
 * %66.2 ya da %75.9 veriyor.
 */

/**
 * Ölçümden gelen oranlar. Referans DIŞ yarıçap: R = 100. Bütün bölümler bu
 * çembere hizalı, kalınlıkları içeri doğru büyüyor.
 */
const SPEC = {
  /** Bölüm kalınlığı = (2 + indis)% — ölçüm 3.03 / 3.96 / 4.95 / 6.03,
      doğrusal uydurma 2.000 + 0.9976·i, sapma en fazla 0.3px. */
  thick0: 2,
  thickStep: 1,
  /** Kalan yolun ince rayı — o da dış çembere hizalı. */
  trackWidth: 1.2,
  /** Bölümler arası beyaz boşluk — AÇI değil yay UZUNLUĞU. */
  gap: 1.31,
  /** Yeşil topuzun yarıçapı; yörüngesi baş bölümünün orta çizgisi. */
  knobR: 4.158,
  /** Sayı: rakam boyu, % boyu, %'nin taban çizgisinden yükselişi. */
  digitCap: 16.34,
  unitCap: 5.617,
  unitRise: 11.83,
  /** Sayının taban çizgisi — çember merkezinin bu kadar üstünde. */
  baseline: 47.27,
} as const;

/**
 * Bölüm opaklıkları (beyaz üstüne siyah). 1, 2 ve 3 ölçüm: 235 / 214 / 188
 * gri değerleri. 0, 4 ve 5 ekran görüntüsünde görünmüyor — biri kadrajın
 * dışında, ikisi değerin ötesinde kaldı — ve rampanın kendi eğrisinden
 * (alpha ≈ 0.078 · i^1.1) uzatıldı. Baş her hâlükârda siyah, yani 4 ve 5
 * ancak değer daha ileri giderse görünür.
 */
const ALPHA = [0, 0.078, 0.161, 0.263, 0.36, 0.46];

/**
 * Plus Jakarta Sans'ta rakam yüksekliğinin punto'ya oranı — `font-size` bundan
 * geri hesaplanıyor, çünkü CSS rakam boyunu doğrudan almıyor. Değer tahmin
 * değil: bilinen bir punto ile basılıp rakam kutusu ölçüldü (0.7414).
 */
const CAP_RATIO = 0.741;

const TRACK = '#f8f8f8';
const HEAD = '#000';
const KNOB = '#76f98d';

/** Bölüm rengi. Tabloda karşılığı olmayan indis için rampa uzatılıyor. */
function rampColor(i: number): string {
  const a = ALPHA[i] ?? 0.078 * Math.pow(i, 1.1);
  return `rgba(0, 0, 0, ${a.toFixed(4)})`;
}

const ring: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: stagger.tight } },
};

/** Bölümler soldan sağa çiziliyor — yayın kendi yönünde. */
const drawIn: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: dur.slow, ease: ease.out },
      opacity: { duration: dur.instant },
    },
  },
};

export type GaugeArcProps = {
  /** 0–1 arası doluluk. */
  value: number;
  /** Yayın başlangıç ve bitiş açısı — saat 12'den saat yönünde, derece. */
  start?: number;
  end?: number;
  /** Yayın kaç eşit bölüme ayrıldığı. */
  segments?: number;
  /**
   * Sayıyı yazıya çeviren biçimlendirici. Verilmezse yüzde, iki ondalık ve
   * aktif dilin ayırıcısı — ekran görüntüsündeki "71,74" bu.
   */
  format?: (value: number) => string;
  /** Ondalık basamak — yalnızca varsayılan biçimlendirici için. */
  fractionDigits?: number;
  /** Sayının yanındaki üst simge. */
  unit?: string;
  /** Ekran okuyucu metni. */
  label: string;
  className?: string;
};

export function GaugeArc({
  value,
  /* Ölçümden. Üç sınır (−61.66 / −23.89 / +13.88) ve dolgunun bittiği açı
     (+25.67) dört kısıt; başlangıç ile adım bunlara birlikte oturtuldu —
     sapma en fazla 0.14°. Yay sol altta başlayıp sağda saat 3 hizasında
     bitiyor, yani simetrik DEĞİL. */
  start = -137.42,
  end = 89.72,
  segments = 6,
  format,
  fractionDigits = 2,
  unit = '%',
  label,
  className,
}: GaugeArcProps) {
  const R = 100;
  const locale = useLocale();
  const rootRef = useRef<HTMLDivElement>(null);
  const numRef = useRef<SVGTSpanElement>(null);
  const inView = useInView(rootRef, { once: true, amount: 0.4 });

  /* Biçimlendirici bir kez kuruluyor: sayı yukarı sayarken her karede yeni
     `Intl.NumberFormat` yaratmak boşuna çöp üretir. */
  const fmt = useMemo(() => {
    if (format) return format;
    const nf = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return (v: number) => nf.format(v * 100);
  }, [format, locale, fractionDigits]);

  const geo = useMemo(() => {
    const sweep = end - start;
    const pitch = sweep / segments;
    const gapDeg = lengthToDeg(R, SPEC.gap);
    /** Dolgunun bittiği açı — değerin kendisi. */
    const vAngle = start + value * sweep;

    /* Kalınlık içeri büyüdüğü için çizgi ortası dış çemberden yarım kalınlık
       içeride: dış hiza bütün bölümlerde ortak. */
    const thick = (i: number) => SPEC.thick0 + i * SPEC.thickStep;
    const orbit = (t: number) => R - t / 2;

    /* Değerin düştüğü bölüm. Tam sınıra denk gelirse bir önceki sayılır;
       aksi hâlde sıfır uzunlukta bir "baş" çizilirdi. */
    const head = Math.min(segments - 1, Math.max(0, Math.ceil((vAngle - start) / pitch) - 1));

    const filled: { d: string; w: number; c: string }[] = [];
    for (let i = 0; i <= head; i++) {
      const isHead = i === head;
      const t = thick(i);
      const a0 = start + i * pitch + gapDeg / 2;
      const a1 = isHead ? vAngle : start + (i + 1) * pitch - gapDeg / 2;
      if (a1 <= a0) continue;
      filled.push({ d: arcPath(0, 0, orbit(t), a0, a1), w: t, c: isHead ? HEAD : rampColor(i) });
    }

    /* Topuz baş bölümünün orta çizgisinde ve dolgudan yarım boşluk ileride;
       açısal yarıçapı kendi yörüngesinden hesaplanıyor. */
    const knobOrbit = orbit(thick(head));
    const knobDeg = lengthToDeg(knobOrbit, SPEC.knobR);
    const knobAngle = vAngle + gapDeg / 2 + knobDeg;
    const knob = polar(0, 0, knobOrbit, knobAngle);

    const trackFrom = knobAngle + knobDeg + gapDeg / 2;
    const box = arcBox(0, 0, R, start, end, [{ x: knob.x, y: knob.y, r: SPEC.knobR }]);

    return {
      filled,
      track:
        trackFrom < end
          ? arcPath(0, 0, orbit(SPEC.trackWidth), trackFrom, end)
          : null,
      knob,
      travel: knobAngle - start,
      box,
    };
  }, [value, start, end, segments]);

  /* Sayı yukarı sayıyor. Motion değeri React durumuna bağlanmıyor: her karede
     yeniden render etmek yerine metin düğümü doğrudan yazılıyor.

     CANLI DEĞER (Dalga 5b, sohbetin canlı üretim kartı): değer saniyeler
     içinde %64 → %65 → %66 diye ilerliyor. Her değişimde 0'dan yeniden
     saymak sayıyı her saniye sıfıra düşürüp geri getirirdi; ilk görünüşte
     0'dan, sonra GÖSTERİLEN son değerden kısa bir geçişle. */
  const shownRef = useRef(0);
  useEffect(() => {
    if (!inView) return;
    const node = numRef.current;
    if (!node) return;
    const from = shownRef.current;
    const c = animate(from, value, {
      duration: from === 0 ? dur.ambient : dur.base,
      ease: ease.out,
      onUpdate: (v) => {
        shownRef.current = v;
        node.textContent = fmt(v);
      },
    });
    return () => c.stop();
  }, [inView, value, fmt]);

  const pad = 0.6;
  const vb = {
    x: geo.box.x - pad,
    y: geo.box.y - pad,
    w: geo.box.w + pad * 2,
    h: geo.box.h + pad * 2,
  };

  return (
    <motion.div
      ref={rootRef}
      role="img"
      aria-label={label}
      className={className ? `garc ${className}` : 'garc'}
      variants={ring}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
    >
      <svg className="garc-svg" viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} fill="none" aria-hidden="true">
        {geo.track && (
          <motion.path
            d={geo.track}
            stroke={TRACK}
            strokeWidth={SPEC.trackWidth}
            strokeLinecap="butt"
            variants={drawIn}
          />
        )}

        {geo.filled.map((s, i) => (
          <motion.path
            key={i}
            d={s.d}
            stroke={s.c}
            strokeWidth={s.w}
            strokeLinecap="butt"
            variants={drawIn}
          />
        ))}

        {/* Topuz yayın başından değerine dönerek geliyor — dönüş bir transform.
            `originX/originY` şart: Motion SVG'de dönme merkezini varsayılan
            olarak öğenin KENDİ sınır kutusundan alıyor, o da topuzun kendi
            merkezi — daire yerinde dönerdi. Geometrinin merkezi (0,0) olduğu
            için ikisi de sıfır. */}
        <motion.g
          style={{ originX: 0, originY: 0 }}
          variants={{
            hidden: { rotate: -geo.travel, opacity: 0 },
            show: { rotate: 0, opacity: 1, transition: springFade(spring.soft) },
          }}
        >
          <circle cx={geo.knob.x} cy={geo.knob.y} r={SPEC.knobR} fill={KNOB} />
        </motion.g>

        {/* Sayı SVG'nin içinde: taban çizgisi ölçümden geliyor ve `<text>`
            zaten taban çizgisine oturuyor. HTML katmanında aynı hizayı
            tutturmak font yükseklik metriklerini tahmin etmek olurdu. */}
        <motion.text
          className="garc-value"
          x={0}
          y={-SPEC.baseline}
          textAnchor="middle"
          fontSize={SPEC.digitCap / CAP_RATIO}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { duration: dur.base, ease: ease.out } },
          }}
        >
          <tspan ref={numRef}>{fmt(value)}</tspan>
          <tspan fontSize={SPEC.unitCap / CAP_RATIO} dy={-SPEC.unitRise}>
            {unit}
          </tspan>
        </motion.text>
      </svg>
    </motion.div>
  );
}
